// Basamak gruplandırmalı sayı input'u — tr-TR locale (1.234,56 formatı).
// Display: formatlı (focus dışı), Edit: raw (focus'ta).
// onChange callback'i raw number value alır (string).
//
// Kullanım:
//   <NumberInput value={ln.quantity} onChange={v => updateLine(i, 'quantity', v)}
//                decimals={3} disabled={!canEdit} style={...} />
//
// Props:
//   value            string | number — kontrol değer
//   onChange(v)      raw string ile çağrılır ('1234.56' gibi — backend uyumlu)
//   decimals         ondalık basamak (default: 2)
//   disabled, style, className, placeholder, onBlur, autoFocus, onFocus
//   align            'right' | 'left' (default 'right' — sayı için doğal)

import React, { useState, useEffect, useRef } from 'react';

const fmtTR = (val, decimals) => {
  if (val === null || val === undefined || val === '') return '';
  const num = parseFloat(String(val).replace(',', '.'));
  if (isNaN(num)) return '';
  return new Intl.NumberFormat('tr-TR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  }).format(num);
};

// '1.234,56' → '1234.56'
const parseTR = (str) => {
  if (str === null || str === undefined || str === '') return '';
  return String(str).replace(/\./g, '').replace(',', '.').replace(/[^\d.\-]/g, '');
};

export default function NumberInput({
  value,
  onChange,
  decimals = 2,
  disabled = false,
  style = {},
  className = '',
  placeholder = '',
  onBlur,
  onFocus,
  align = 'right',
  step,
  ...rest
}) {
  const [focused, setFocused] = useState(false);
  const [editValue, setEditValue] = useState('');
  const ref = useRef(null);

  // value değiştiğinde edit buffer'ı sync et (focus dışındayken)
  useEffect(() => {
    if (!focused) {
      setEditValue(value === null || value === undefined || value === '' ? '' : String(value));
    }
  }, [value, focused]);

  const handleFocus = (e) => {
    setFocused(true);
    // Raw değeri göster (1234.56), seçili olsun (kolay düzenleme)
    setEditValue(value === null || value === undefined || value === '' ? '' : String(value).replace(',', '.'));
    setTimeout(() => { try { e.target.select(); } catch {} }, 0);
    onFocus && onFocus(e);
  };

  const handleBlur = (e) => {
    setFocused(false);
    // Parse + onChange — backend uyumlu raw
    const raw = parseTR(editValue);
    if (raw !== String(value || '')) {
      onChange && onChange(raw);
    }
    onBlur && onBlur(e);
  };

  const handleChange = (e) => {
    let v = e.target.value;
    // Hem . hem , kabul et — display sırasında parseFloat zaten her ikisini de halleder
    // Yalnızca harf ve geçersiz karakterleri at
    v = v.replace(/[^\d.,\-]/g, '');
    setEditValue(v);
    // Anlık değişimi de gönder (lokal state için)
    if (onChange) {
      const raw = parseTR(v);
      onChange(raw);
    }
  };

  const display = focused ? editValue : fmtTR(value, decimals);

  return (
    <input
      ref={ref}
      type="text"
      inputMode="decimal"
      value={display}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      disabled={disabled}
      placeholder={placeholder}
      style={{ textAlign: align, ...style }}
      className={className}
      {...rest}
    />
  );
}
